/**
 * Customer Relationship Utility Functions for TradesMatePro
 * 
 * These functions manage the company_customers relationship table
 * which replaces the direct company_id on customers table.
 * 
 * During migration, both approaches are supported for backward compatibility.
 */

import { supabase } from '../utils/supabaseClient';

import { normalizePhone } from './phone';
/**
 * Get all customers for a company using the company_customers relationship table
 * Uses JOIN to fetch customers via the relationship
 * 
 * @param {string} companyId - The company ID
 * @returns {Promise<{success: boolean, customers?: Array, error?: string}>}
 */
/**
 * Get customer IDs linked to a company via the junction table.
 * This is the source of truth for which customers a company has access to.
 * Used by all read queries instead of customers.company_id.
 *
 * @param {string} companyId
 * @returns {Promise<string[]>} Array of customer UUIDs
 */
export async function getCustomerIdsForCompany(companyId) {
  if (!companyId) return [];
  const { data, error } = await supabase
    .from('company_customers')
    .select('customer_id')
    .eq('company_id', companyId);
  if (error) {
    console.error('❌ Error fetching customer IDs from junction:', error);
    return [];
  }
  return (data || []).map(r => r.customer_id);
}

export async function getCustomersForCompany(companyId) {
  console.log('🔍 getCustomersForCompany called:', { companyId });

  if (!companyId) {
    return { success: false, error: 'Company ID is required' };
  }

  try {
    // The junction (company_customers) is the source of truth, joined in the SAME request: the
    // empty embed only filters and adds no field to the rows. It was two requests — every linked
    // id, then customers?id=in.(…) — with the second waiting on the first. Same rows, columns and
    // ORDER BY (page speed Phase 2, 2026-10-25; tests/e2e/speed/customer-join-equivalence.spec.js).
    const { data, error } = await supabase
      .from('customers')
      .select(`
        id, name, first_name, last_name, company_name, email, phone,
        street_address, billing_address_line_1, billing_city, billing_state, billing_zip_code,
        city, state, zip_code, company_id, created_at, notes, is_active, status, rating,
        customer_since, updated_at, credit_limit, payment_terms, tax_exempt, source, customer_type,
        company_customers!inner()
      `)
      .eq('company_customers.company_id', companyId)
      .order('name', { ascending: true });

    if (error) {
      console.error('❌ Error fetching customers:', error);
      return { success: false, error: error.message };
    }

    // Normalize the data
    const normalized = (data || []).map(customer => {
      return {
        ...customer,
        role: 'customer',
        customer_since: customer.customer_since || customer.created_at,
      };
    });

    console.log(`✅ Found ${normalized.length} customers for company ${companyId}`);
    return { success: true, customers: normalized };
  } catch (err) {
    console.error('❌ Unexpected error in getCustomersForCompany:', err);
    return { success: false, error: err.message || 'Unknown error' };
  }
}

/**
 * Add a customer to a company via the relationship table
 * Handles the "Arlie scenario" - existing customer getting a new relationship
 * 
 * @param {string} companyId - The company ID
 * @param {string} customerId - The customer ID
 * @param {string} role - Role in company (default: 'customer')
 * @param {string} createdBy - User ID who created the relationship
 * @returns {Promise<{success: boolean, relationship?: object, error?: string}>}
 */
export async function addCustomerToCompany(companyId, customerId, role = 'customer', createdBy = null) {
  console.log('🔗 addCustomerToCompany called:', { companyId, customerId, role });

  if (!companyId) {
    return { success: false, error: 'Company ID is required' };
  }

  if (!customerId) {
    return { success: false, error: 'Customer ID is required' };
  }

  try {
    // Check if relationship already exists
    const { data: existing } = await supabase
      .from('company_customers')
      .select('id')
      .eq('company_id', companyId)
      .eq('customer_id', customerId)
      .maybeSingle();

    if (existing) {
      console.log('⚠️ Relationship already exists:', existing.id);
      return { 
        success: true, 
        relationship: existing,
        alreadyExists: true,
        message: 'Customer already linked to this company'
      };
    }

    // Create new relationship
    const payload = {
      company_id: companyId,
      customer_id: customerId,
      role: role,
    };

    if (createdBy) {
      payload.created_by = createdBy;
    }

    const { data, error } = await supabase
      .from('company_customers')
      .insert(payload)
      .select()
      .single();

    if (error) {
      console.error('❌ Error creating relationship:', error);
      return { success: false, error: error.message };
    }

    console.log('✅ Relationship created:', data);
    return { success: true, relationship: data };
  } catch (err) {
    console.error('❌ Unexpected error in addCustomerToCompany:', err);
    return { success: false, error: err.message || 'Unknown error' };
  }
}

/**
 * Remove a customer from a company (remove relationship only, not customer)
 * 
 * @param {string} companyId - The company ID
 * @param {string} customerId - The customer ID
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function removeCustomerFromCompany(companyId, customerId) {
  console.log('🔓 removeCustomerFromCompany called:', { companyId, customerId });

  if (!companyId || !customerId) {
    return { success: false, error: 'Company ID and Customer ID are required' };
  }

  try {
    const { error } = await supabase
      .from('company_customers')
      .delete()
      .eq('company_id', companyId)
      .eq('customer_id', customerId);

    if (error) {
      console.error('❌ Error removing relationship:', error);
      return { success: false, error: error.message };
    }

    console.log('✅ Relationship removed');
    return { success: true };
  } catch (err) {
    console.error('❌ Unexpected error in removeCustomerFromCompany:', err);
    return { success: false, error: err.message || 'Unknown error' };
  }
}

/**
 * A customer's display name from a form's current values, using the SAME precedence as the
 * database. customers.name is GENERATED ALWAYS AS
 *   COALESCE(company_name, first||' '||last, first, last, 'Unnamed Customer')
 * so company_name wins whenever it is present, regardless of customer_type.
 *
 * The two name families are NOT mutually exclusive — 11 of 314 live rows carry both. Every
 * customer form derived this from `type` instead, which disagreed with the column for exactly
 * those rows, and (worse) led each form to NULL the family its type did not claim. That is
 * board #262: switching a customer's type deleted the name, in both directions.
 *
 * Returns '' when nothing is filled in, so callers can test it for "has a name at all".
 */
export function displayNameFromForm(form) {
  const company = (form?.company_name || '').trim();
  if (company) return company;
  return [form?.first_name, form?.last_name].filter(Boolean).join(' ').trim();
}

/**
 * ONE fact, TWO columns: customers.type and customers.customer_type both store the
 * residential/commercial/industrial classification. They are not allowed to disagree, and the
 * app cannot pick a winner at read time — different surfaces read different columns (the
 * Customers table badge reads customer_type; the edit form's radio reads type), so a row where
 * they diverge renders as two different customers depending on where you look.
 *
 * They diverged because the EDIT payload wrote `type` and silently omitted `customer_type`,
 * while the CREATE payload wrote both. Measured live 2026-09-17: 14 of 314 rows disagree, 2
 * have customer_type NULL. Board #262 — Jerry's live test found a row saved as Commercial that
 * still showed the Residential badge, forever.
 *
 * Returning BOTH columns from one derived value is the point: a caller spreads this into its
 * payload and cannot write one without the other, so the next person to add a customer write
 * path does not have to know this paragraph exists.
 *
 * `type` wins over `customer_type` when an existing row already disagrees, because `type` is
 * the column the edit path has been writing — it holds the newer value.
 */
export function customerTypeColumns(source) {
  const raw = source?.type || source?.customer_type || 'residential';
  const value = String(raw).trim().toLowerCase();
  return { type: value, customer_type: value };
}

/**
 * Blank and whitespace are "not provided". customers.first_name / last_name / company_name are
 * all nullable text, so normalise to NULL rather than writing '' and making an empty value
 * indistinguishable from a filled-in one.
 */
export function nameOrNull(value) {
  const trimmed = (value || '').trim();
  return trimmed || null;
}

/**
 * Which first_name / last_name actually get STORED.
 *
 * Two kinds of caller exist. Most send structured names (`nameOrNull(form.first_name)` and
 * friends); some send only a display `name`. Splitting `name` is the fallback for the second
 * kind, and the contract pages/Customers.js:3636 states is that it applies "only ... when the
 * caller passes none".
 *
 * Deciding that PER FIELD — which `customerData.first_name || first_name` did — is not that
 * rule, and the gap stored wrong data (board #565, reproduced by Jerry on the live site).
 * Enter "   " as First Name and "Smith" as Last Name: nameOrNull correctly sends
 * first_name: null, but displayNameFromForm keeps "   " as truthy, so `name` joins and trims
 * down to "Smith". The fallback then split "Smith" into the first name, and `null || "Smith"`
 * wrote the LAST name into the FIRST name column — both columns came out "Smith".
 *
 * So the question is asked ONCE, for the pair: a deliberate null on one side can never be
 * refilled from the other side's text.
 *
 * Company display names are never split into person names. Explicit contact-person names
 * still belong in first_name / last_name alongside company_name.
 */
export function resolveCustomerNames(customerData = {}) {
  const callerGaveNames = nameOrNull(customerData.first_name) !== null
    || nameOrNull(customerData.last_name) !== null;

  if (callerGaveNames) {
    return {
      first_name: nameOrNull(customerData.first_name),
      last_name: nameOrNull(customerData.last_name),
    };
  }

  const isCompanyCustomer = !!(customerData.company_name && String(customerData.company_name).trim());
  if (isCompanyCustomer) {
    return { first_name: null, last_name: null };
  }

  const nameParts = (customerData.name || '').trim().split(' ');
  return {
    first_name: nameParts[0] || null,
    last_name: nameParts.slice(1).join(' ') || null,
  };
}

/**
 * Create or link a customer to a company
 * This is the main function for AddCustomerModal - handles both new and existing customers
 *
 * @param {string} companyId - The company ID
 * @param {object} customerData - Customer data (name, email, phone, address, etc.)
 * @param {string} createdBy - User ID who created the customer
 * @returns {Promise<{success: boolean, customer?: object, isNewCustomer?: boolean, error?: string}>}
 */
export async function createOrLinkCustomer(companyId, customerData, createdBy = null) {
  console.log('📝 createOrLinkCustomer called:', { companyId, customerData });

  if (!companyId) {
    return { success: false, error: 'Company ID is required' };
  }

  if (!customerData || !customerData.name?.trim()) {
    return { success: false, error: 'Customer name is required' };
  }

  try {
    const { first_name, last_name } = resolveCustomerNames(customerData);

    // Check if customer with this email already exists (case-insensitive)
    let customer = null;
    if (customerData.email) {
      const { data: existingCustomer } = await supabase
        .from('customers')
        .select('id, email, first_name, last_name')
        .ilike('email', customerData.email)
        .maybeSingle();

      if (existingCustomer) {
        console.log('📧 Found existing customer with same email:', existingCustomer.id);
        customer = existingCustomer;

        // Check if already linked to this company
        const { data: existingRel } = await supabase
          .from('company_customers')
          .select('id')
          .eq('company_id', companyId)
          .eq('customer_id', customer.id)
          .maybeSingle();

        if (existingRel) {
          console.log('⚠️ Customer already linked to this company');
          return {
            success: false,
            customer: customer,
            error: 'This customer is already linked to your company',
            alreadyLinked: true
          };
        }
      }
    }

    // If no existing customer, create via atomic RPC
    // RPC runs as SECURITY DEFINER — no RLS issues with INSERT+SELECT
    if (!customer) {
      console.log('📝 Creating new customer via RPC');
      const { data: rpcResult, error: rpcError } = await supabase.rpc('create_customer_with_relationship', {
        p_company_id: companyId,
        p_first_name: first_name,
        p_last_name: last_name,
        p_company_name: customerData.company_name || null,
        p_email: customerData.email || null,
        // Validate at the boundary. This is the shared creation path for
        // AddCustomerModal, GoLiveStep and QuoteBuilder, and it used to pass whatever
        // was in the box straight to the RPC — which is how a street address ended up
        // in a customer's phone column (fae1acdb, 2026-08-30).
        p_phone: normalizePhone(customerData.phone),
        p_mobile_phone: normalizePhone(customerData.mobile_phone),
        p_type: customerData.type || 'residential',
        p_preferred_contact: customerData.preferred_contact || null,
        p_source: customerData.source || customerData.created_via || 'internal',
        p_notes: customerData.notes || null,
        p_credit_limit: customerData.credit_limit ? parseFloat(customerData.credit_limit) : null,
        p_payment_terms: customerData.payment_terms || null,
        p_tax_exempt: customerData.tax_exempt || false,
        p_billing_address_line_1: customerData.billing_address_line_1 || customerData.address || null,
        p_billing_address_line_2: customerData.billing_address_line_2 || null,
        p_billing_city: customerData.billing_city || customerData.city || null,
        p_billing_state: customerData.billing_state || customerData.state || null,
        p_billing_zip_code: customerData.billing_zip_code || customerData.zip_code || null,
        p_billing_country: customerData.billing_country || null,
        p_street_address: customerData.street_address || customerData.billing_address_line_1 || customerData.address || null,
        p_customer_type: customerData.customer_type || null,
        p_created_via: customerData.created_via || 'internal',
        p_created_by: createdBy || null,
      });

      if (rpcError) {
        console.error('❌ RPC create_customer_with_relationship failed:', rpcError);
        return { success: false, error: rpcError.message };
      }

      if (!rpcResult?.success) {
        return {
          success: false,
          error: rpcResult?.error || 'Failed to create customer',
          alreadyLinked: rpcResult?.already_linked || false,
        };
      }

      // Fetch full customer (junction now exists, RLS allows it)
      const { data: fullCustomer } = await supabase
        .from('customers')
        .select('*')
        .eq('id', rpcResult.customer_id)
        .maybeSingle();

      console.log('✅ Customer created:', rpcResult.customer_id, rpcResult.is_new ? '(new)' : '(existing linked)');

      return {
        success: true,
        customer: fullCustomer || { id: rpcResult.customer_id },
        isNewCustomer: rpcResult.is_new,
      };
    }

    // Existing customer found by email — just create the junction
    const relResult = await addCustomerToCompany(companyId, customer.id, 'customer', createdBy);

    if (!relResult.success && !relResult.alreadyExists) {
      return {
        success: false,
        customer: customer,
        error: relResult.error
      };
    }

    return {
      success: true,
      customer: customer,
      isNewCustomer: false,
      relationship: relResult.relationship
    };
  } catch (err) {
    console.error('❌ Unexpected error in createOrLinkCustomer:', err);
    return { success: false, error: err.message || 'Unknown error' };
  }
}

/**
 * Check if a customer is linked to a company
 * 
 * @param {string} companyId - The company ID
 * @param {string} customerId - The customer ID
 * @returns {Promise<{success: boolean, isLinked?: boolean, error?: string}>}
 */
export async function isCustomerLinkedToCompany(companyId, customerId) {
  if (!companyId || !customerId) {
    return { success: false, error: 'Company ID and Customer ID are required' };
  }

  try {
    const { data, error } = await supabase
      .from('company_customers')
      .select('id')
      .eq('company_id', companyId)
      .eq('customer_id', customerId)
      .single();

    if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
      return { success: false, error: error.message };
    }

    return { success: true, isLinked: !!data };
  } catch (err) {
    return { success: false, error: err.message };
  }
}

/**
 * Get all companies a customer is linked to
 * 
 * @param {string} customerId - The customer ID
 * @returns {Promise<{success: boolean, companies?: Array, error?: string}>}
 */
export async function getCustomerCompanies(customerId) {
  if (!customerId) {
    return { success: false, error: 'Customer ID is required' };
  }

  try {
    const { data, error } = await supabase
      .from('company_customers')
      .select(`
        id, company_id, customer_id, role, created_at,
        companies:company_id(id, name, logo_url)
      `)
      .eq('customer_id', customerId);

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true, companies: data || [] };
  } catch (err) {
    return { success: false, error: err.message };
  }
}

/**
 * Get customers for company using legacy company_id (for backward compatibility)
 * This uses the old method - should be phased out over time
 * 
 * @param {string} companyId - The company ID
 * @returns {Promise<{success: boolean, customers?: Array, error?: string}>}
 */
export async function getCustomersForCompanyLegacy(companyId) {
  console.log('🔍 getCustomersForCompanyLegacy called (deprecated):', { companyId });

  if (!companyId) {
    return { success: false, error: 'Company ID is required' };
  }

  try {
    // Phase 2: Use junction table
    const customerIds = await getCustomerIdsForCompany(companyId);
    if (customerIds.length === 0) return { success: true, customers: [] };

    const { data, error } = await supabase
      .from('customers')
      .select('*')
      .in('id', customerIds)
      .order('name', { ascending: true });

    if (error) {
      return { success: false, error: error.message };
    }

    return { success: true, customers: data || [] };
  } catch (err) {
    return { success: false, error: err.message };
  }
}
